/* eslint-disable max-len */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    CCard, CCardBody, CTable, CTableHead, CTableRow, CTableHeaderCell,
    CTableBody, CTableDataCell, CSpinner, CAlert,
    CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CButton,
    CPagination, CPaginationItem, CTooltip, CBadge, CFormInput, CFormLabel,
    CRow, CCol, CCollapse, CFormSelect
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPencil, cilTrash, cilArrowTop, cilArrowBottom, cilChevronDoubleDown, cilChevronRight, cilPlus, cilFilter, cilX, cilSearch } from '@coreui/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { getSampleListRequest, deleteSampleRequest, createWeaveFromSampleRequest } from '../../store/sample/sampleActions';
import { getCompositions, getWeaveTypes } from '../../services/endpoints';
import { mainColors } from '../../helpers/colors';
import { formatDateOnly, formatDateForBackend } from '../../helpers/dateHelper';
import ExportData from '../ExportData/ExportData';
import './SampleTable.scss';

const SampleTable = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Redux state
    const { sampleList, isLoading: loading, error: storeError, totalCount, totalPages, createWeaveFromSampleLoading } = useSelector(state => state.sampleReducer);

    const [samples, setSamples] = useState([]);
    const [error, setError] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
    const [imageModal, setImageModal] = useState({ open: false, src: null, alt: '' });
    const [createWeaveModal, setCreateWeaveModal] = useState({ open: false, item: null });
    const [weaveCode, setWeaveCode] = useState('');

    // Server-side Pagination state
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Server-side Sorting state
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('insertdatetime');

    // Filter state
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        CardNo: '',
        CompanyName: '',
        CompositionGuid: '',
        WeaveTypeGuid: '',
        MinFabricWidth: '',
        MaxFabricWidth: '',
        MinFabricWeight: '',
        MaxFabricWeight: '',
        StartDate: '',
        EndDate: ''
    });

    // Dropdown options
    const [compositions, setCompositions] = useState([]);
    const [weaveTypes, setWeaveTypes] = useState([]);

    // Expandable rows state
    const [expandedRows, setExpandedRows] = useState(new Set());

    const toNullableInt = (value) => {
        const raw = String(value ?? '').trim();
        if (!raw) return null;
        const parsed = parseInt(raw, 10);
        return Number.isNaN(parsed) ? null : parsed;
    };

    const formatDensityDisplay = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        const numericValue = Number(String(value).replace(',', '.'));
        if (!Number.isFinite(numericValue)) return value;
        const scaledValue = Math.round(Math.abs(numericValue));
        const sign = numericValue < 0 ? '-' : '';
        const whole = Math.trunc(scaledValue / 100);
        const decimal = (scaledValue % 100).toString().padStart(2, '0');
        return `${sign}${whole},${decimal}`;
    };

    const handleWholeInput = (e, field) => {
        const value = e.target.value.replace(/\D/g, '');
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(1);
    };

    const toggleRowExpansion = (sampleId) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(sampleId)) {
            newExpandedRows.delete(sampleId);
        } else {
            newExpandedRows.add(sampleId);
        }
        setExpandedRows(newExpandedRows);
    };

    // Load dropdown options
    useEffect(() => {
        const loadDropdowns = async () => {
            try {
                const [compositionRes, weaveTypeRes] = await Promise.all([
                    getCompositions(),
                    getWeaveTypes()
                ]);

                if (compositionRes?.data?.CompositionList) {
                    setCompositions(compositionRes.data.CompositionList);
                }
                if (weaveTypeRes?.data?.WeaveTypeList) {
                    setWeaveTypes(weaveTypeRes.data.WeaveTypeList);
                }
            } catch (err) {
                console.error('Error loading dropdowns:', err);
            }
        };
        loadDropdowns();
    }, []);

    // Update local samples when Redux state changes
    useEffect(() => {
        setSamples(sampleList);
    }, [sampleList]);

    // Update error from store
    useEffect(() => {
        if (storeError) {
            setError(storeError?.message || 'Bir hata oluştu');
        }
    }, [storeError]);

    // Load data with filters using Redux
    const loadData = useCallback(() => {

        const payload = {
            CardNo: filters.CardNo || null,
            CompanyName: filters.CompanyName || null,
            CompositionGuid: filters.CompositionGuid ? parseFloat(filters.CompositionGuid) : null,
            WeaveTypeGuid: filters.WeaveTypeGuid ? parseFloat(filters.WeaveTypeGuid) : null,
            MinFabricWidth: toNullableInt(filters.MinFabricWidth),
            MaxFabricWidth: toNullableInt(filters.MaxFabricWidth),
            MinFabricWeight: toNullableInt(filters.MinFabricWeight),
            MaxFabricWeight: toNullableInt(filters.MaxFabricWeight),
            StartDate: filters.StartDate ? parseInt(formatDateForBackend(filters.StartDate), 10) : null,
            EndDate: filters.EndDate ? parseInt(formatDateForBackend(filters.EndDate), 10) : null,
            OrderBy: orderBy,
            IsDescending: order === 'desc',
            PageNumber: page,
            PageSize: rowsPerPage
        };

        dispatch(getSampleListRequest(payload));
    }, [dispatch, JSON.stringify(filters), orderBy, order, page, rowsPerPage]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleEdit = (sample) => {
        const id = sample.SampleId || sample.SampleGuid || sample.PrdSampleGuid;
        navigate(`/samples/edit/${id}`);
    };

    const handleDeleteClick = (sample) => {
        setDeleteModal({ open: true, item: sample });
    };

    const handleDeleteConfirm = () => {
        if (!deleteModal.item) return;

        const sessionUserId = localStorage.getItem('sessionUserId');
        const payload = {
            SessionUserId: sessionUserId,
            SampleId: deleteModal.item.SampleId ||
                deleteModal.item.SampleGuid || deleteModal.item.PrdSampleGuid
        };

        dispatch(deleteSampleRequest(payload));
        setDeleteModal({ open: false, item: null });
    };

    // Create Weave from Sample
    const handleCreateWeaveClick = (sample) => {
        setCreateWeaveModal({ open: true, item: sample });
        setWeaveCode('');
    };

    const handleCreateWeaveConfirm = () => {
        if (!createWeaveModal.item || !weaveCode.trim()) return;

        const sessionUserId = localStorage.getItem('sessionUserId');
        const payload = {
            SessionUserId: sessionUserId,
            PrdSampleGuid: createWeaveModal.item.SampleId ||
                createWeaveModal.item.SampleGuid || createWeaveModal.item.PrdSampleGuid,
            WeaveCode: weaveCode.trim()
        };

        // Dispatch Redux action - saga will handle success/error toast
        dispatch(createWeaveFromSampleRequest(payload));
        setCreateWeaveModal({ open: false, item: null });
        setWeaveCode('');
    };

    // Format decimal values (stored as int * 100)
    const formatDecimal = (value) => {
        if (!value && value !== 0) return '-';
        return (parseFloat(value) / 100).toFixed(2);
    };

    // Get currency symbol from sample data
    const getCurrencySymbol = (sample) => {
        const currencyName = (sample.CurrencyName || '').toLowerCase();
        const currencyCode = (sample.CurrencyCode || '').toLowerCase();

        // Check by code first
        if (currencyCode === 'eur' || currencyCode === 'euro') return '€';
        if (currencyCode === 'usd' || currencyCode === 'dollar') return '$';
        if (currencyCode === 'try' || currencyCode === 'tl' ||
            currencyCode === 'turkish lira') return '₺';
        if (currencyCode === 'gbp' || currencyCode === 'pound') return '£';
        if (currencyCode === 'jpy' || currencyCode === 'yen') return '¥';
        if (currencyCode === 'cny' || currencyCode === 'yuan') return '¥';
        if (currencyCode === 'inr' || currencyCode === 'rupee') return '₹';
        if (currencyCode === 'rub' || currencyCode === 'ruble') return '₽';
        if (currencyCode === 'chf' || currencyCode === 'franc') return '₣';
        if (currencyCode === 'krw' || currencyCode === 'won') return '₩';
        if (currencyCode === 'thb' || currencyCode === 'baht') return 'à¸¿';
        if (currencyCode === 'pln' || currencyCode === 'zloty') return 'zł';
        if (currencyCode === 'uah' || currencyCode === 'hryvnia') return '₴';
        if (currencyCode === 'egp') return 'E£';
        if (currencyCode === 'sar') return '﷼';
        if (currencyCode === 'bgn') return 'лв';

        // Check by name
        if (currencyName.includes('euro') || currencyName.includes('eur')) return '€';
        if (currencyName.includes('dolar') || currencyName.includes('dollar') ||
            currencyName.includes('usd')) return '$';
        if (currencyName.includes('türk') || currencyName.includes('turk') ||
            currencyName.includes('try') || currencyName.includes('tl') ||
            currencyName.includes('lira')) return '₺';
        if (currencyName.includes('pound') || currencyName.includes('sterlin')) return '£';
        if (currencyName.includes('yen')) return '¥';
        if (currencyName.includes('yuan')) return '¥';
        if (currencyName.includes('rupee')) return '₹';
        if (currencyName.includes('ruble')) return '₽';
        if (currencyName.includes('franc') || currencyName.includes('frank')) return '₣';
        if (currencyName.includes('won')) return '₩';
        if (currencyName.includes('baht')) return 'à¸¿';
        if (currencyName.includes('zloty') || currencyName.includes('zloti')) return 'zł';
        if (currencyName.includes('hryvnia') || currencyName.includes('grivna')) return '₴';
        if (currencyName.includes('mısır') || currencyName.includes('misir') ||
            currencyName.includes('egypt')) return 'E£';
        if (currencyName.includes('suudi') || currencyName.includes('saudi') ||
            currencyName.includes('riyal') || currencyName.includes('arabistan')) return '﷼';
        if (currencyName.includes('bulgar') || currencyName.includes('leva') ||
            currencyName.includes('лев')) return 'лв';

        return '';
    };

    // Server-side sorting - map column id to backend OrderBy value
    const getOrderByValue = (columnId) => {
        const orderByMap = {
            'CardNo': 'cardno',
            'CompanyName': 'companyname',
            'FabricWidth': 'fabricwidth',
            'FabricWeight': 'fabricweight',
            'Price': 'price',
            'Datetime': 'date'
        };
        return orderByMap[columnId] || 'date';
    };

    const handleRequestSort = (property) => {
        const backendOrderBy = getOrderByValue(property);
        if (orderBy === backendOrderBy) {
            // Toggle direction if same column
            setOrder(order === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(backendOrderBy);
            setOrder('desc');
        }
        setPage(1); // Reset to first page when sorting changes
    };

    // Filter handlers
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setPage(1); // Reset to first page when filter changes
    };

    const clearFilters = () => {
        setFilters({
            CardNo: '',
            CompanyName: '',
            CompositionGuid: '',
            WeaveTypeGuid: '',
            MinFabricWidth: '',
            MaxFabricWidth: '',
            MinFabricWeight: '',
            MaxFabricWeight: '',
            StartDate: '',
            EndDate: ''
        });
        setPage(1);
    };

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    const activeFilterCount = Object.values(filters).filter(value => String(value || '').trim() !== '').length;

    // Pagination handlers
    const handleChangePage = (newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (value) => {
        setRowsPerPage(value);
        setPage(1);
    };

    // Generate pagination items
    const paginationItems = [];
    const maxVisiblePages = 7;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
        paginationItems.push(
            <CPaginationItem key="first" onClick={() => handleChangePage(1)}>
                1
            </CPaginationItem>
        );
        if (startPage > 2) {
            paginationItems.push(
                <CPaginationItem key="start-ellipsis" disabled>
                    ...
                </CPaginationItem>
            );
        }
    }

    for (let i = startPage; i <= endPage; i++) {
        paginationItems.push(
            <CPaginationItem key={i} active={i === page} onClick={() => handleChangePage(i)}>
                {i}
            </CPaginationItem>
        );
    }

    if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
            paginationItems.push(
                <CPaginationItem key="end-ellipsis" disabled>
                    ...
                </CPaginationItem>
            );
        }
        paginationItems.push(
            <CPaginationItem key="last" onClick={() => handleChangePage(totalPages)}>
                {totalPages}
            </CPaginationItem>
        );
    }

    // Column definitions
    const columns = [
        { id: 'expand', label: '', sortable: false },
        { id: 'rowNumber', label: t('rowNumber') || 'Sıra', sortable: false },
        { id: 'Photo', label: t('sampleImage'), sortable: false },
        { id: 'CardNo', label: t('cardNo'), sortable: true },
        { id: 'CompanyName', label: t('companyName'), sortable: true },
        { id: 'FabricWidth', label: t('fabricWidth'), sortable: true },
        { id: 'FabricWeight', label: t('fabricWeight'), sortable: true },
        { id: 'Price', label: t('price'), sortable: true },
        { id: 'Datetime', label: t('date'), sortable: true },
        { id: 'actions', label: t('actions'), sortable: false },
    ];

    // Export columns - exclude control columns and include additional fields
    const exportColumns = useMemo(() => [
        { id: 'CardNo', label: t('cardNo') },
        { id: 'CompanyName', label: t('companyName') },
        { id: 'FabricWidth', label: t('fabricWidth') },
        { id: 'FabricWeight', label: t('fabricWeight') },
        { id: 'Price', label: t('price') },
        { id: 'Datetime', label: t('date') },
        { id: 'TotalThread', label: t('totalThreads') || 'Çözgü Tel Adedi' },
        { id: 'ReedWidth', label: t('reedWidth') || 'Tarak Eni' },
        { id: 'ReedNo', label: t('reedNo') || 'Tarak No' },
        { id: 'WarpDensity', label: t('warpDensityi') || ' Mamül Çözgü Sıklığı' },
        { id: 'WeftDensity', label: t('weftDensityi') || 'Mamül Atkı Sıklığı' },
        { id: 'Dgts', label: t('dgts') || 'DGTS' },
    ], [t]);

    // Build cell value for export
    const buildCellValue = (colId, item) => {
        switch (colId) {
            case 'FabricWeight':
                return item.FabricWeight ?? '-';
            case 'Price': {
                const priceFormatted = formatDecimal(item.Price);
                const symbol = getCurrencySymbol(item);
                return symbol ? `${priceFormatted} ${symbol}` : priceFormatted;
            }
            case 'Datetime':
                return formatDateOnly(item.Datetime) || '-';
            case 'CardNo':
            case 'CompanyName':
            case 'FabricWidth':
            case 'TotalThread':
            case 'ReedWidth':
            case 'ReedNo':
            case 'WarpDensity':
            case 'WeftDensity':
            case 'Dgts':
                return item[colId] ?? '-';
            default:
                return item[colId] ?? '';
        }
    };


    if (error) {
        return (
            <CAlert color="danger" dismissible onClose={() => setError(null)}>
                {error}
            </CAlert>
        );
    }

    // Check if a column is currently being sorted (for icon display)
    const isColumnSorted = (columnId) => {
        const columnOrderBy = getOrderByValue(columnId);
        return orderBy === columnOrderBy;
    };

    return (
        <div className="sample-table-container">
            <CCard>
                <CCardBody>
                    {/* Filter Controls */}
                    <div className="filter-controls mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-2">
                                <CButton
                                    color={showFilters ? "primary" : "outline-primary"}
                                    variant={showFilters ? "solid" : "outline"}
                                    size="sm"
                                    onClick={toggleFilters}
                                    className="filter-toggle-btn"
                                >
                                    <CIcon icon={cilFilter} size="sm" className="me-1" />
                                    {t('filters') || 'Filtreler'}
                                    {activeFilterCount > 0 && (
                                        <span className="filter-count-badge ms-1">{activeFilterCount}</span>
                                    )}
                                </CButton>
                                {activeFilterCount > 0 && (
                                    <CButton
                                        color="outline-secondary"
                                        variant="outline"
                                        size="sm"
                                        onClick={clearFilters}
                                        className="clear-filters-btn"
                                    >
                                        <CIcon icon={cilX} size="sm" className="me-1" />
                                        {t('clearFilters') || 'Filtreleri Temizle'}
                                    </CButton>
                                )}
                            </div>
                            <div className="results-info d-flex align-items-center gap-2">
                                <span className="text-muted">
                                    {totalCount} {t('resultsFound') || 'sonuç bulundu'}
                                </span>
                                <ExportData
                                    columns={exportColumns}
                                    rows={samples}
                                    allRows={samples}
                                    buildCellValue={buildCellValue}
                                    filenamePrefix="samples_export"
                                    triggerDisabled={!samples || samples.length === 0}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Filter Panel */}
                    <CCollapse visible={showFilters}>
                        <div className="filter-panel mb-4">
                            <CRow className="g-3">
                                {/* Card No */}
                                <CCol xs={12} sm={6} md={4} lg={3}>
                                    <div className="filter-field">
                                        <label className="filter-label">{t('cardNo') || 'Kart No'}</label>
                                        <div className="filter-input-wrapper">
                                            <CIcon icon={cilSearch} className="filter-input-icon" />
                                            <CFormInput
                                                type="text"
                                                placeholder={`${t('search') || 'Ara'} ${t('cardNo') || 'kart no'}...`}
                                                value={filters.CardNo}
                                                onChange={(e) => handleFilterChange('CardNo', e.target.value)}
                                                className="filter-input"
                                                size="sm"
                                            />
                                            {filters.CardNo && (
                                                <CButton
                                                    color="link"
                                                    size="sm"
                                                    className="filter-clear-btn"
                                                    onClick={() => handleFilterChange('CardNo', '')}
                                                >
                                                    <CIcon icon={cilX} size="sm" />
                                                </CButton>
                                            )}
                                        </div>
                                    </div>
                                </CCol>

                                {/* Company Name */}
                                <CCol xs={12} sm={6} md={4} lg={3}>
                                    <div className="filter-field">
                                        <label className="filter-label">{t('companyName') || 'Firma Adı'}</label>
                                        <div className="filter-input-wrapper">
                                            <CIcon icon={cilSearch} className="filter-input-icon" />
                                            <CFormInput
                                                type="text"
                                                placeholder={`${t('search') || 'Ara'} ${t('companyName') || 'firma adı'}...`}
                                                value={filters.CompanyName}
                                                onChange={(e) => handleFilterChange('CompanyName', e.target.value)}
                                                className="filter-input"
                                                size="sm"
                                            />
                                            {filters.CompanyName && (
                                                <CButton
                                                    color="link"
                                                    size="sm"
                                                    className="filter-clear-btn"
                                                    onClick={() => handleFilterChange('CompanyName', '')}
                                                >
                                                    <CIcon icon={cilX} size="sm" />
                                                </CButton>
                                            )}
                                        </div>
                                    </div>
                                </CCol>

                                {/* Composition Dropdown */}
                                <CCol xs={12} sm={6} md={4} lg={3}>
                                    <div className="filter-field">
                                        <label className="filter-label">{t('composition') || 'Kompozisyon'}</label>
                                        <CFormSelect
                                            size="sm"
                                            value={filters.CompositionGuid}
                                            onChange={(e) => handleFilterChange('CompositionGuid', e.target.value)}
                                            className="filter-select"
                                        >
                                            <option value="">{t('all') || 'Tümü'}</option>
                                            {compositions.map((comp, i) => (
                                                <option
                                                    key={comp.PrdCompositionGuid || comp.CompositionGuid || comp.CompositionId || `comp-${i}`}
                                                    value={comp.PrdCompositionGuid || comp.CompositionGuid || comp.CompositionId || ''}
                                                >
                                                    {comp.ThreadName || comp.CompositionName}
                                                </option>
                                            ))}
                                        </CFormSelect>
                                    </div>
                                </CCol>

                                {/* Weave Type Dropdown */}
                                <CCol xs={12} sm={6} md={4} lg={3}>
                                    <div className="filter-field">
                                        <label className="filter-label">{t('weaveType') || 'Örgü Tipi'}</label>
                                        <CFormSelect
                                            size="sm"
                                            value={filters.WeaveTypeGuid}
                                            onChange={(e) => handleFilterChange('WeaveTypeGuid', e.target.value)}
                                            className="filter-select"
                                        >
                                            <option value="">{t('all') || 'Tümü'}</option>
                                            {weaveTypes.map((wt, i) => (
                                                <option
                                                    key={wt.PrdWeaveTypeGuid || wt.WeaveTypeGuid || wt.WeaveTypeId || `wt-${i}`}
                                                    value={wt.PrdWeaveTypeGuid || wt.WeaveTypeGuid || wt.WeaveTypeId || ''}
                                                >
                                                    {wt.WeaveTypeName}
                                                </option>
                                            ))}
                                        </CFormSelect>
                                    </div>
                                </CCol>

                                {/* Min Fabric Width */}
                                <CCol xs={12} sm={6} md={4} lg={3}>
                                    <div className="filter-field">
                                        <label className="filter-label">{`${t('min')} ${t('fabricWidth')}`}</label>
                                        <div className="filter-input-wrapper">
                                            <CIcon icon={cilSearch} className="filter-input-icon" />
                                            <CFormInput
                                                type="text"
                                                placeholder={`${t('search') || 'Ara'} ${String(t('fabricWidth')).toLowerCase()}...`}
                                                value={filters.MinFabricWidth}
                                                onChange={(e) => handleWholeInput(e, 'MinFabricWidth')}
                                                className="filter-input"
                                                size="sm"
                                            />
                                        </div>
                                    </div>
                                </CCol>

                                {/* Max Fabric Width */}
                                <CCol xs={12} sm={6} md={4} lg={3}>
                                    <div className="filter-field">
                                        <label className="filter-label">{`${t('max')} ${t('fabricWidth')}`}</label>
                                        <div className="filter-input-wrapper">
                                            <CIcon icon={cilSearch} className="filter-input-icon" />
                                            <CFormInput
                                                type="text"
                                                placeholder={`${t('search') || 'Ara'} ${String(t('fabricWidth')).toLowerCase()}...`}
                                                value={filters.MaxFabricWidth}
                                                onChange={(e) => handleWholeInput(e, 'MaxFabricWidth')}
                                                className="filter-input"
                                                size="sm"
                                            />
                                        </div>
                                    </div>
                                </CCol>

                                {/* Min Fabric Weight */}
                                <CCol xs={12} sm={6} md={4} lg={3}>
                                    <div className="filter-field">
                                        <label className="filter-label">{`${t('min')} ${t('fabricWeight')}`}</label>
                                        <div className="filter-input-wrapper">
                                            <CIcon icon={cilSearch} className="filter-input-icon" />
                                            <CFormInput
                                                type="text"
                                                placeholder={`${t('search') || 'Ara'} ${String(t('fabricWeight')).toLowerCase()}...`}
                                                value={filters.MinFabricWeight}
                                                onChange={(e) => handleWholeInput(e, 'MinFabricWeight')}
                                                className="filter-input"
                                                size="sm"
                                            />
                                        </div>
                                    </div>
                                </CCol>

                                {/* Max Fabric Weight */}
                                <CCol xs={12} sm={6} md={4} lg={3}>
                                    <div className="filter-field">
                                        <label className="filter-label">{`${t('max')} ${t('fabricWeight')}`}</label>
                                        <div className="filter-input-wrapper">
                                            <CIcon icon={cilSearch} className="filter-input-icon" />
                                            <CFormInput
                                                type="text"
                                                placeholder={`${t('search') || 'Ara'} ${String(t('fabricWeight')).toLowerCase()}...`}
                                                value={filters.MaxFabricWeight}
                                                onChange={(e) => handleWholeInput(e, 'MaxFabricWeight')}
                                                className="filter-input"
                                                size="sm"
                                            />
                                        </div>
                                    </div>
                                </CCol>

                                {/* Start Date */}
                                <CCol xs={12} sm={6} md={4} lg={3}>
                                    <div className="filter-field">
                                        <label className="filter-label">{t('startDate') || 'Başlangıç Tarihi'}</label>
                                        <CFormInput
                                            type="date"
                                            value={filters.StartDate}
                                            onChange={(e) => handleFilterChange('StartDate', e.target.value)}
                                            className="filter-input"
                                            size="sm"
                                        />
                                    </div>
                                </CCol>

                                {/* End Date */}
                                <CCol xs={12} sm={6} md={4} lg={3}>
                                    <div className="filter-field">
                                        <label className="filter-label">{t('endDate') || 'Bitiş Tarihi'}</label>
                                        <CFormInput
                                            type="date"
                                            value={filters.EndDate}
                                            onChange={(e) => handleFilterChange('EndDate', e.target.value)}
                                            className="filter-input"
                                            size="sm"
                                        />
                                    </div>
                                </CCol>
                            </CRow>
                        </div>
                    </CCollapse>

                    {loading && (
                        <div className="text-center py-3">
                            <CSpinner color="primary" size="sm" />
                        </div>
                    )}

                    <div className="table-wrapper">
                        <CTable hover className="sample-table">
                            <CTableHead>
                                <CTableRow>
                                    {columns.map((column) => (
                                        <CTableHeaderCell
                                            key={column.id}
                                            className={`table-header-cell ${column.id === 'Photo' ? 'image-col' : ''} ${column.id === 'actions' ? 'actions-col' : ''}`}
                                            style={{ cursor: column.sortable ? 'pointer' : 'default' }}
                                            onClick={() => column.sortable && handleRequestSort(column.id)}
                                        >
                                            {column.label}
                                            {column.sortable && isColumnSorted(column.id) && (
                                                <CIcon icon={order === 'asc' ? cilArrowTop : cilArrowBottom} size="sm" className="ms-1" />
                                            )}
                                        </CTableHeaderCell>
                                    ))}
                                </CTableRow>
                            </CTableHead>
                            <CTableBody>
                                {samples.length === 0 ? (
                                    <CTableRow>
                                        <CTableDataCell colSpan={columns.length} className="text-center py-4">
                                            <div className="text-muted">{t('noDataFound') || 'Veri bulunamadı'}</div>
                                        </CTableDataCell>
                                    </CTableRow>
                                ) : (
                                    samples.map((sample, idx) => {
                                        const sampleId = sample.SampleId || sample.SampleGuid || sample.PrdSampleGuid;
                                        const isExpanded = expandedRows.has(sampleId);
                                        return (
                                            <React.Fragment key={sampleId || idx}>
                                                <CTableRow className={isExpanded ? 'expanded-row' : ''}>
                                                    <CTableDataCell className="expand-cell">
                                                        <div
                                                            className="expand-button"
                                                            onClick={() => toggleRowExpansion(sampleId)}
                                                        >
                                                            <CIcon
                                                                icon={isExpanded ? cilChevronDoubleDown : cilChevronRight}
                                                                size="sm"
                                                                className="expand-icon"
                                                            />
                                                        </div>
                                                    </CTableDataCell>
                                                    <CTableDataCell className="row-number-col">
                                                        {(page - 1) * rowsPerPage + idx + 1}
                                                    </CTableDataCell>
                                                    <CTableDataCell className="image-col">
                                                        {sample.Photo ? (
                                                            <img
                                                                src={sample.Photo}
                                                                alt={sample.CardNo || 'Sample'}
                                                                className="sample-thumbnail clickable"
                                                                onClick={() => setImageModal({
                                                                    open: true,
                                                                    src: sample.Photo,
                                                                    alt: sample.CardNo || 'Sample'
                                                                })}
                                                            />
                                                        ) : (
                                                            <div className="no-image">-</div>
                                                        )}
                                                    </CTableDataCell>
                                                    <CTableDataCell>
                                                        <CBadge color="primary" shape="rounded-pill">
                                                            {sample.CardNo || '-'}
                                                        </CBadge>
                                                    </CTableDataCell>
                                                    <CTableDataCell>{sample.CompanyName || '-'}</CTableDataCell>
                                                    <CTableDataCell>{sample.FabricWidth || '-'}</CTableDataCell>
                                                    <CTableDataCell>{sample.FabricWeight ?? '-'}</CTableDataCell>
                                                    <CTableDataCell>
                                                        {formatDecimal(sample.Price)} {getCurrencySymbol(sample)}
                                                    </CTableDataCell>
                                                    <CTableDataCell>{formatDateOnly(sample.Datetime) || '-'}</CTableDataCell>
                                                    <CTableDataCell className="actions-col">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <CTooltip content={t('edit')}>
                                                                <div className="cursor-pointer p-1" onClick={() => handleEdit(sample)}>
                                                                    <CIcon icon={cilPencil} size="sm" style={{ color: mainColors.primary }} />
                                                                </div>
                                                            </CTooltip>
                                                            <CTooltip content={t('createProduct') || 'Ürün Oluştur'}>
                                                                <div className="cursor-pointer p-1" onClick={() => handleCreateWeaveClick(sample)}>
                                                                    <CIcon icon={cilPlus} size="sm" style={{ color: '#28a745' }} />
                                                                </div>
                                                            </CTooltip>
                                                            <CTooltip content={t('delete')}>
                                                                <div className="cursor-pointer p-1" onClick={() => handleDeleteClick(sample)}>
                                                                    <CIcon icon={cilTrash} size="sm" style={{ color: '#dc3545' }} />
                                                                </div>
                                                            </CTooltip>
                                                        </div>
                                                    </CTableDataCell>
                                                </CTableRow>

                                                {isExpanded && (
                                                    <CTableRow className="detail-row">
                                                        <CTableDataCell colSpan={columns.length} className="detail-cell">
                                                            <div className="detail-content">
                                                                <div className="detail-grid">
                                                                    <div className="detail-item">
                                                                        <span className="detail-label">{t('totalThreads') || 'Çözgü Tel Adedi'}:</span>
                                                                        <span className="detail-value">{sample.TotalThread || '-'}</span>
                                                                    </div>
                                                                    <div className="detail-item">
                                                                        <span className="detail-label">{t('reedWidth') || 'Tarak Eni'}:</span>
                                                                        <span className="detail-value">{sample.ReedWidth || '-'}</span>
                                                                    </div>
                                                                    <div className="detail-item">
                                                                        <span className="detail-label">{t('reedNo') || 'Tarak No'}:</span>
                                                                        <span className="detail-value">{sample.ReedNo || '-'}</span>
                                                                    </div>
                                                                    <div className="detail-item">
                                                                        <span className="detail-label">{t('warpDensityi') || 'Mamül Çözgü Sıklığı'}:</span>
                                                                        <span className="detail-value">{formatDensityDisplay(sample.WarpDensity)}</span>
                                                                    </div>
                                                                    <div className="detail-item">
                                                                        <span className="detail-label">{t('weftDensityi') || 'Mamül Atkı Sıklığı'}:</span>
                                                                        <span className="detail-value">{formatDensityDisplay(sample.WeftDensity)}</span>
                                                                    </div>
                                                                    <div className="detail-item">
                                                                        <span className="detail-label">{t('dgts') || 'DGTS'}:</span>
                                                                        <span className="detail-value">{sample.Dgts || '-'}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </CTableDataCell>
                                                    </CTableRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </CTableBody>
                        </CTable>
                    </div>

                    {/* Pagination */}
                    {totalCount > 0 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted">{t('show') || 'Göster'}:</span>
                                <select
                                    className="form-select form-select-sm"
                                    value={rowsPerPage}
                                    onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))}
                                    style={{ width: 'auto' }}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select>
                                <span className="text-muted">{t('entries') || 'kayıt'}</span>
                            </div>
                            <CPagination className="mb-0">
                                <CPaginationItem
                                    aria-label={t('previous') || 'Önceki'}
                                    onClick={() => page > 1 && handleChangePage(page - 1)}
                                    disabled={page === 1}
                                >
                                    &laquo;
                                </CPaginationItem>
                                {paginationItems}
                                <CPaginationItem
                                    aria-label={t('next') || 'Sonraki'}
                                    onClick={() => page < totalPages && handleChangePage(page + 1)}
                                    disabled={page === totalPages || totalPages === 0}
                                >
                                    &raquo;
                                </CPaginationItem>
                            </CPagination>
                            <div className="text-muted">
                                {t('showing') || 'Gösterilen'} {Math.min((page - 1) * rowsPerPage + 1, totalCount)} {t('to') || '-'}{' '}
                                {Math.min(page * rowsPerPage, totalCount)} {t('of') || '/'} {totalCount} {t('entries') || 'kayıt'}
                            </div>
                        </div>
                    )}
                </CCardBody>
            </CCard>

            {/* Delete Confirmation Modal */}
            <CModal visible={deleteModal.open} onClose={() => setDeleteModal({ open: false, item: null })}>
                <CModalHeader>
                    <CModalTitle>{t('deleteSample')}</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <p>{t('confirmDeleteSample')}</p>
                    {deleteModal.item && (
                        <div className="bg-light p-3 rounded">
                            <strong>{t('cardNo')}:</strong> {deleteModal.item.CardNo}<br />
                            <strong>{t('companyName')}:</strong> {deleteModal.item.CompanyName}
                        </div>
                    )}
                </CModalBody>
                <CModalFooter>
                    <CButton color="secondary" onClick={() => setDeleteModal({ open: false, item: null })}>
                        {t('cancel')}
                    </CButton>
                    <CButton color="danger" onClick={handleDeleteConfirm}>
                        {t('delete')}
                    </CButton>
                </CModalFooter>
            </CModal>

            {/* Image Preview Modal */}
            <CModal
                visible={imageModal.open}
                onClose={() => setImageModal({ open: false, src: null, alt: '' })}
                size="lg"
                alignment="center"
                className="image-preview-modal"
            >
                <CModalHeader closeButton>
                    <CModalTitle>{imageModal.alt || t('sampleImage')}</CModalTitle>
                </CModalHeader>
                <CModalBody className="text-center p-0">
                    {imageModal.src && (
                        <img
                            src={imageModal.src}
                            alt={imageModal.alt}
                            className="preview-image"
                        />
                    )}
                </CModalBody>
            </CModal>

            {/* Create Weave from Sample Modal */}
            <CModal
                visible={createWeaveModal.open}
                onClose={() => { setCreateWeaveModal({ open: false, item: null }); setWeaveCode(''); }}
            >
                <CModalHeader>
                    <CModalTitle>{t('createProduct') || 'Ürün Oluştur'}</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <p>{t('createWeaveFromSampleDescription') || 'Bu Analizden ürün oluşturmak için tip kodunu girin.'}</p>
                    {createWeaveModal.item && (
                        <div className="bg-light p-3 rounded mb-3">
                            <strong>{t('cardNo')}:</strong> {createWeaveModal.item.CardNo}<br />
                            <strong>{t('companyName')}:</strong> {createWeaveModal.item.CompanyName}
                        </div>
                    )}
                    <div className="mb-3">
                        <CFormLabel htmlFor="weaveCode">{t('weaveCode') || 'Tip Kodu'} *</CFormLabel>
                        <CFormInput
                            id="weaveCode"
                            type="text"
                            value={weaveCode}
                            onChange={(e) => setWeaveCode(e.target.value)}
                            placeholder={t('enterWeaveCode') || 'Tip kodu girin'}
                            autoFocus
                        />
                    </div>
                </CModalBody>
                <CModalFooter>
                    <CButton
                        color="secondary"
                        onClick={() => { setCreateWeaveModal({ open: false, item: null }); setWeaveCode(''); }}
                    >
                        {t('cancel')}
                    </CButton>
                    <CButton
                        color="success"
                        onClick={handleCreateWeaveConfirm}
                        disabled={!weaveCode.trim() || createWeaveFromSampleLoading}
                    >
                        {createWeaveFromSampleLoading ? (
                            <>
                                <CSpinner size="sm" className="me-2" />
                                {t('creating') || 'Oluşturuluyor...'}
                            </>
                        ) : (
                            t('create') || 'Oluştur'
                        )}
                    </CButton>
                </CModalFooter>
            </CModal>
        </div>
    );
};

export default SampleTable;
